import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';

import { insuranceSlotsForLoan } from '@/components/agreements/boardSlots';
import {
    AgreementSealingProvider,
    useAgreementSealing,
} from '@/components/agreements/useAgreementSealing';
import { useContractAgreements } from '@/components/agreements/useContractAgreements';
import { supabase } from '@/integrations/supabase/client';
import type { Attestation } from '@/lib/certificatePrefill';
import { captureError } from '@/lib/logger';

import { focusJumpTarget, useDossierChrome } from '@/components/contracts/setup/dossier/useDossierChrome';
import CommitBar from '@/components/contracts/setup/inline/CommitBar';
import {
    useCertificateRow,
    type CertificateCommit,
    type CertificateRowModel,
} from '@/components/contracts/setup/inline/CertificateRow';
import Manifest from '@/components/contracts/setup/manifest/Manifest';
import NarratorLine from '@/components/contracts/setup/manifest/NarratorLine';
import { journeyLabel } from '@/components/contracts/setup/manifest/manifestView';
import { policyPeriodText } from '@/components/contracts/setup/data/policyDraft';
import { areaPath, type SetupArea } from '@/components/contracts/setup/steps';
import type { SetupProgress } from '@/components/contracts/setup/setupState';
import {
    applyInsuranceEdit,
    draftDepositTotal,
} from '@/components/contracts/setup/insurance/insuranceArithmetic';
import {
    attributeInsuranceRefusal,
    createInsurance,
    deletePolicy,
    insuranceInvalidationKeys,
    NO_VEHICLE_REFUSAL,
    saveAdvanceArea,
    savePolicyArea,
    savePremiumArea,
    validateDraft,
    type InsuranceSaveContext,
} from '@/components/contracts/setup/insurance/insuranceCommit';
import { resolveInsuranceDeepLink } from '@/components/contracts/setup/insurance/insuranceDeepLink';
import {
    insuranceAreaDirty,
    insuranceAreaFields,
    insuranceFormWith,
    AREA_FIELDS,
    type InsuranceField,
    type InsuranceFormValues,
} from '@/components/contracts/setup/insurance/insuranceForm';
import {
    insuranceHeading,
    type InsuranceRow,
    type InsuranceRowId,
} from '@/components/contracts/setup/insurance/insuranceState';
import {
    INSURANCE_AREAS,
    INSURANCE_AREA_KEYS,
    INSURANCE_AREA_LABELS,
    insuranceAreaPath,
    parsePolicyParam,
    resolveInsuranceArea,
    type InsuranceArea,
} from '@/components/contracts/setup/insurance/insuranceSteps';
import { useInsuranceState } from '@/components/contracts/setup/insurance/useInsuranceState';
import AdvanceArea from '@/components/contracts/setup/insurance/areas/AdvanceArea';
import InsuranceAgreementArea from '@/components/contracts/setup/insurance/areas/InsuranceAgreementArea';
import PolicyArea from '@/components/contracts/setup/insurance/areas/PolicyArea';
import PremiumArea from '@/components/contracts/setup/insurance/areas/PremiumArea';
import CreateInsuranceConfirm from '@/components/contracts/setup/insurance/manifest/CreateInsuranceConfirm';

/** What the rail's draft mark says for a field an edit EMPTIED — the mark needs a word to draw. */
const CLEARED = 'cleared';

/**
 * WHAT THE CREATE BAR SAYS ABOUT AN OFFER STANDING UN-TICKED, and it is not the server's sentence.
 *
 * A certificate nothing could read has a door out of it [D-B56], and the row's display refusal has
 * already stood down for exactly that case — so a bar quoting `It cannot be filed against this
 * contract.` would contradict the offer printed one block above it. What is missing is the tick.
 */
const CERTIFICATE_UNTICKED =
    'Confirm what the certificate says, or take the document back out.';

/**
 * EVERY FIELD A SIGNED INSTRUMENT PUTS BEYOND WRITING [F7] — what the freeze drops from `edits`.
 *
 * TWO AREAS, FOR TWO DIFFERENT SHAPES OF THE SAME RULE. The Policy canvas keeps its bar and loses
 * only the period (`policyValues` names neither date column once frozen), so its entry here is the
 * two dates and nothing else — the insured value and the certificate are still writable. The
 * Advance canvas loses the bar ENTIRELY (`areaCommit`'s `frozen && target === 'advance'`), so every
 * field it owns is beyond writing and the whole list goes; it is taken from `AREA_FIELDS` rather
 * than retyped, so a seventh advance field arrives here with the canvas that gained it.
 */
const FROZEN_DROPPED_FIELDS: InsuranceField[] = ['startsAt', 'expiresAt', ...AREA_FIELDS.advance];

/**
 * The insurance policy dossier — the contract dossier's shape over one `insurance_contracts`
 * junction, and the side sheet's replacement.
 *
 * ONE ROUTE, ONE SUBJECT, FOUR AREAS. `/dashboard/contracts/:id/insurance/:policyId/:area?` serves
 * the create flow (`:policyId` is the literal `new`) and every existing year of cover, and the area
 * lives in the PATH for `AdvanceSetup`'s reasons: a merge-field pre-flight links to a place, a
 * half-finished policy is bookmarkable, and Back walks the areas rather than the history of one
 * screen. A bare path resolves to a real area and replaces itself once the reads have landed,
 * through a ref latch that fires exactly once — so a background refetch can never move an admin off
 * the area they are typing into.
 *
 * IT IS THE SAME SHELL AS THE OTHER TWO DOSSIERS AND NOT A COPY OF IT. `Manifest`, `NarratorLine`,
 * `useDossierChrome`, the `.setup-dossier` grid and the vaul journey sheet are the contract
 * dossier's own, generic over the row and area vocabularies; `.sd-no-ledger` drops the grid's third
 * track, because a policy has no statistics column to keep and a three-track grid with nothing in
 * the third leaves a dead column where the canvas's measure should be.
 *
 * THE CREATE IS ONE SEQUENCE THAT RESUMES AS AN EDIT, WHICH IS THE WHOLE OF WHAT THIS PAGE ADDS
 * OVER ITS TWIN. The side sheet fired nine unwrapped round trips from the top on every retry, so a
 * mid-save failure left a policy and a junction behind and the second attempt wrote another pair.
 * `createInsurance` refuses first, rolls back exactly the two writes whose failure leaves something
 * nothing references, and calls back the moment the junction lands — `onIdentity` below. From that
 * moment the subject exists, the URL names it, the staged certificate is filed and every remaining
 * statement is an ordinary area save on a policy that is already there.
 *
 * THE CERTIFICATE ROW IS MOUNTED HERE, ONCE, AND NOT ON THE POLICY CANVAS. `ocrOnNextFiledRead` is
 * a module-global one-shot (invariant 13), the row holds a read in flight and a published triple
 * that must survive an area switch, and the create bar on the ADVANCE canvas asks this row for its
 * refusal — none of which a hook mounted on a canvas that unmounts could answer.
 *
 * `junctionScope: 'all'` IS THE ONLY SCOPE THAT MINTS THIS POLICY'S SLOT [D-B17]. The setup board
 * mints insurance slots for the id-earliest junction alone, so a renewal year has no row at all on
 * that canvas; this page asks for the board Contract Insights asks for, which is the board every
 * year of cover has a row on.
 *
 * There is no new permission: the route sits under `/dashboard/contracts/...`, and `canAccessRoute`
 * matches on the first path segment.
 */
export default function InsuranceSetup() {
    const { contractId: contractIdParam, policyId: policyIdParam, area: areaParam } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const contractId = parseInt(contractIdParam || '0', 10);
    /** `parseInt('new')` is `NaN`, and this is why nothing here ever calls it — see `parsePolicyParam`. */
    const policySubject = parsePolicyParam(policyIdParam);
    const routeArea = resolveInsuranceArea(areaParam);

    const { rootRef } = useDossierChrome<HTMLDivElement>();
    const [activeRowId, setActiveRowId] = useState<InsuranceRowId | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [edits, setEdits] = useState<Partial<InsuranceFormValues>>({});
    /** The server's own refusal of the last save, by area, attributed to this area's fields. */
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<InsuranceArea, Record<string, string>>>
    >({});
    /** `delete_insurance_cascade`'s refusal, printed verbatim inside the confirm it was raised from. */
    const [deleteRefusal, setDeleteRefusal] = useState<string | null>(null);

    const policyId: number | 'new' = policySubject ?? 'new';
    const isNew = policyId === 'new';
    const mode: 'create' | 'edit' = isNew ? 'create' : 'edit';

    /**
     * THE ADDRESS NAMES SOMETHING THAT IS NOT A POLICY — one answer, at either depth.
     *
     * `policyId` falls back to `'new'` for a segment `parsePolicyParam` refuses, which is right for
     * the word `new` and wrong for `abc`. The error screen below catches it, but the landing latch
     * runs first and has only `routeArea` to stop it: `/insurance/abc/policy` would show the error
     * while `/insurance/abc` replaced itself into the create flow, with the bad id gone from the bar.
     */
    const badSubject =
        policySubject === null && policyIdParam !== undefined && policyIdParam.toLowerCase() !== 'new';

    const agreements = useContractAgreements({ contractId, junctionScope: 'all' });

    const sealing = useAgreementSealing({
        documents: agreements.documents,
        onRefresh: agreements.refreshBoard,
    });

    /**
     * Unsaved edits, by rail row — what draws the blue "draft" mark in the manifest.
     *
     * NEVER COUNTED AS PROGRESS. A typed figure is not a saved one, and a rail that graded the form
     * would tell an admin a policy was ready while nothing had been written.
     *
     * PRESENCE, NOT TRUTHINESS — AND CLEARING IS A WORD, exactly as on the advance dossier: an edit
     * that EMPTIES a box is an edit, and the mark is drawn from a non-empty string.
     *
     * THE ATTESTATION HAS NO ROW AND THE STAGED FILE MARKS ONLY WHEN THERE IS ONE. A confirmation
     * is a word given ABOUT a document rather than an edit of the policy (`AREA_DIRTY_FIELDS`), and
     * `onFileChange(null)` — Remove — puts the row back to the document already on file, which is
     * no change at all.
     */
    const drafts = useMemo<Partial<Record<InsuranceRowId, string>>>(() => {
        const map: Partial<Record<InsuranceRowId, string>> = {};
        if (edits.valuedAt !== undefined) map['pol-value'] = edits.valuedAt || CLEARED;
        if (edits.startsAt !== undefined || edits.expiresAt !== undefined) {
            // ONE ROW FOR TWO FIELDS, AND ONLY THE ENDS THAT MOVED. `policyPeriodText` needs both
            // ends and answers null for either one missing — which on a period half-edited would
            // print `cleared` over a date that is perfectly well set.
            map['pol-period'] =
                [edits.startsAt, edits.expiresAt].filter(Boolean).join(' — ') || CLEARED;
        }
        if (edits.certificateFile) map['pol-certificate'] = edits.certificateFile.name;
        if (edits.premiumTransactionId !== undefined) {
            map['pol-premium'] = edits.premiumTransactionId || CLEARED;
        }
        if (edits.discountTransactionId !== undefined) {
            map['pol-discount'] = edits.discountTransactionId || CLEARED;
        }
        if (edits.premiumAmount !== undefined) map['pol-amount'] = edits.premiumAmount || CLEARED;
        if (edits.interestRate !== undefined) map['pol-rate'] = edits.interestRate || CLEARED;
        if (edits.depositTransactionId !== undefined) {
            map['pol-deposit'] = edits.depositTransactionId || CLEARED;
        }
        if (edits.weeklyAmount !== undefined) map['pol-weekly'] = edits.weeklyAmount || CLEARED;
        if (edits.firstStartsAt !== undefined) map['pol-first'] = edits.firstStartsAt || CLEARED;
        return map;
    }, [edits]);

    const {
        state,
        snapshot,
        stateReady,
        landingArea,
        junctionMissing,
        saved,
        form,
        draft,
        instrument,
        existing,
        otherDeposits,
        hpClass,
        forward,
        frozen,
        vehicle,
        certificate: filedCertificate,
        policyLoading,
        policyRefetching,
    } = useInsuranceState({ contractId, policyId, agreements, edits, drafts });

    const refusals = useMemo(
        () => validateDraft(draft, mode, existing, { hpClass, otherDeposits }),
        [draft, mode, existing, hpClass, otherDeposits],
    );

    /** The weekly recovery is a row in the database, not a plan typed in this session [D-B45]. */
    const instalmentSaved = existing?.instalmentId !== null && existing?.instalmentId !== undefined;

    /**
     * WHAT THE ONE EDIT DOOR NEEDS AND CANNOT TAKE AS A DEPENDENCY — the `live` idiom, three values
     * wide.
     *
     * `onChange` is the only route into `edits`, and the certificate row demands that the three
     * callbacks built on it be referentially stable: its publish and release effects compare a
     * stored handle against the current one, and a new function identity every render would restart
     * that comparison. So the rule's inputs are read through a ref that is assigned at render, and
     * the door itself has no dependencies at all.
     */
    const live = useRef({ saved, instalmentSaved, otherDeposits, frozen });
    live.current = { saved, instalmentSaved, otherDeposits, frozen };

    /**
     * EVERY PATCH THIS PAGE MAKES GOES THROUGH `applyInsuranceEdit` [D-B45].
     *
     * An admin who types a weekly recovery and then links a payment covering the whole premium has
     * described two contradictory years — nothing advanced, and yet something recovered weekly — and
     * the rule drops the TYPED pair on exactly that edge, never a recovery already running. One door
     * is what makes that true of every control on the page rather than of the ones that remembered.
     *
     * THE PATCH IS THE DELTA AND NOT THE WHOLE FORM. `edits` is a patch over the server's values, so
     * what is stored is what was TOUCHED: the caller's own updates, plus the two weekly fields where
     * — and only where — the rule cleared them.
     */
    const onChange = useCallback((updates: Partial<InsuranceFormValues>) => {
        // THE LAST REFUSAL LEAVES ON THE FIRST EDIT AFTER IT, which is the inline groups' own rule:
        // a sentence about a value the admin has since changed is a sentence about nothing, and the
        // save is what answers again. Returning the same object where there is nothing to clear
        // keeps this off the render path of every keystroke.
        setFieldErrors((current) => (Object.keys(current).length === 0 ? current : {}));
        setEdits((current) => {
            const context = live.current;
            const previous = insuranceFormWith(context.saved, current);
            const next = applyInsuranceEdit(previous, updates, {
                instalmentSaved: context.instalmentSaved,
                otherDeposits: context.otherDeposits,
            });
            const patch: Partial<InsuranceFormValues> = { ...current, ...updates };
            if (next.weeklyAmount !== previous.weeklyAmount) patch.weeklyAmount = next.weeklyAmount;
            if (next.firstStartsAt !== previous.firstStartsAt) {
                patch.firstStartsAt = next.firstStartsAt;
            }
            return patch;
        });
    }, []);

    /**
     * The certificate row's three writers, every one of them the door above. Stable because it is.
     *
     * AND THE DATES STOP AT THIS DOOR ON A FROZEN YEAR [F7]. The row applies a read's period without
     * anybody clicking — `settle` and `putBackBeforeANewRead` both reach `onDateChange` — so on a
     * year whose instrument is signed, picking a certificate would push two dates into `edits` that
     * `policyValues` then drops: a draft mark on the rail and a period on screen that no save can
     * ever write. It is refused HERE rather than by a `frozen` prop on the row because the row is
     * shared with contract setup, which has no freeze to tell it about; and `frozen` is read off
     * `live` rather than taken as a dependency because the row requires this callback to keep one
     * identity for the life of the page.
     */
    const onDateChange = useCallback((field: 'startsAt' | 'expiresAt', value: string) => {
        if (live.current.frozen) return;
        onChange({ [field]: value });
    }, [onChange]);
    const onFileChange = useCallback(
        (file: File | null) => onChange({ certificateFile: file }),
        [onChange],
    );
    const onAttestation = useCallback(
        (next: Attestation | null) => onChange({ attestation: next }),
        [onChange],
    );

    const certificateDates = useMemo(
        () => ({ startsAt: form.startsAt, expiresAt: form.expiresAt }),
        [form.startsAt, form.expiresAt],
    );

    /**
     * HOW THIS PAGE'S LAST WRITE ENDED, in the three facts the certificate row acts on.
     *
     * A held read is released onto a REFUSAL and dropped onto a landed commit, so the row needs to
     * tell two endings apart — which is what `seq` is for. It moves on every ending; the row records
     * only the committed ones, because a refusal is precisely the ending on which the held read is
     * applied.
     *
     * A CREATE COMMITS AT `onIdentity` AND NOT AT THE END OF THE SEQUENCE. The certificate is filed
     * before the policy row exists, so by the time the junction lands the bytes are on the server
     * and the staged file is no longer the subject of anything.
     */
    const [certificateEnding, setCertificateEnding] = useState<{
        outcome: CertificateCommit['outcome'];
        seq: number;
    }>({ outcome: 'idle', seq: 0 });

    const invalidate = useCallback(() => {
        for (const key of insuranceInvalidationKeys(contractId)) {
            queryClient.invalidateQueries({ queryKey: key });
        }
        agreements.refreshBoard();
    }, [queryClient, contractId, agreements]);

    const goToArea = useCallback(
        (area: InsuranceArea) => navigate(insuranceAreaPath(contractId, policyId, area)),
        [navigate, contractId, policyId],
    );

    const activeArea = routeArea ?? landingArea;

    /**
     * The refusal of the last save, attributed to a field — or to nothing, which is also an answer.
     *
     * ONE MAP PER AREA because two of the sentences collide: "That payment is already recorded
     * against another policy." is raised by the premium write and by the deposit write, and the
     * sentence alone does not name a field. An unattributable message lands nowhere and is read on
     * the toast, which is where an unattributable refusal belongs.
     */
    const attributeTo = useCallback((area: InsuranceArea, message: string | null | undefined) => {
        setFieldErrors({ [area]: attributeInsuranceRefusal(area, message) ?? {} });
    }, []);

    /**
     * A CREATE'S REFUSAL MAY BELONG TO ANY OF THE THREE, because the sequence writes all of them —
     * so every map is asked and the one that claims the sentence keeps it.
     */
    const attributeAcrossAreas = useCallback((message: string | null | undefined) => {
        const next: Partial<Record<InsuranceArea, Record<string, string>>> = {};
        for (const area of INSURANCE_AREA_KEYS) {
            const fields = attributeInsuranceRefusal(area, message);
            if (fields) next[area] = fields;
        }
        setFieldErrors(next);
    }, []);

    /**
     * THE POLICY THIS PAGE CREATED, AND WHY IT IS A REF.
     *
     * It is read by the subject-reset effect on the very render the route changes under it, and by
     * the create's own `onSuccess` after the last statement lands. State would be a render behind
     * the navigation it is about.
     */
    const createdId = useRef<number | null>(null);

    /**
     * WHAT THE CERTIFICATE ROW ANSWERS, READ FROM THE MUTATIONS THAT CANNOT SEE IT YET.
     *
     * The row needs this page's mutation state (`saving`) and the mutations need the row's answer
     * (`attestationFor`), which is a cycle in declaration order and not in time: the mutation's body
     * runs on a click, long after the row has been built. Assigned at render, read only from the
     * handlers — the `live` idiom, one object wide.
     */
    const certificateRef = useRef<CertificateRowModel | null>(null);

    /** The vehicle a policy is written over and a certificate is checked against. */
    const requireVehicle = useCallback(() => {
        if (vehicle.id === null) throw new Error(NO_VEHICLE_REFUSAL);
        return { id: vehicle.id, plate: vehicle.plate, vin: vehicle.vin };
    }, [vehicle]);

    /**
     * THE CREATE — refused before it is attempted, proof-read before it is confirmed, and resumed as
     * an edit the moment the subject exists [ruling 4].
     *
     * `onIdentity` FIRES BETWEEN THE JUNCTION AND THE PREMIUM. The confirm closes, the filed bytes
     * leave `edits` (they are on the server; a second save must not upload them again), the row is
     * told its commit landed, and the route is REPLACED with the new policy's — keeping the area the
     * admin is standing on, so nothing moves underneath them. The subject-reset effect skips exactly
     * this hop, so the figures still to be written stay in `edits` and the area bars come back to
     * save whatever the rest of the sequence could not.
     */
    const create = useMutation({
        mutationFn: () => {
            // UNREACHABLE ONCE THE ROUTE NAMES A POLICY. After the hop this page is an edit screen,
            // and a second run of the sequence would insert a second policy and a second junction —
            // which is precisely the side sheet's defect.
            if (mode !== 'create' || createdId.current !== null) {
                throw new Error('This policy has already been created.');
            }
            const subject = requireVehicle();
            return createInsurance(
                supabase,
                {
                    ...draft,
                    // ASKED OF THE DRAFT BEING SAVED, not of a render-time flag — the row's own rule.
                    attestation: certificateRef.current?.attestationFor(certificateDates) ?? null,
                    contractId,
                    vehicleId: subject.id,
                    vehicle: { plate: subject.plate, vin: subject.vin },
                    // WHETHER THIS PREMIUM MAY BE FINANCED WITHOUT A WEEKLY RECOVERY [D-B57] — a
                    // fact about the contract, which the create has no save context to carry.
                    hpClass,
                },
                {
                    onIdentity: ({ policyId: newPolicyId }) => {
                        setConfirmOpen(false);
                        createdId.current = newPolicyId;
                        setEdits((current) => {
                            const next = { ...current };
                            delete next.certificateFile;
                            delete next.attestation;
                            return next;
                        });
                        setCertificateEnding((ending) => ({
                            outcome: 'committed',
                            seq: ending.seq + 1,
                        }));
                        navigate(insuranceAreaPath(contractId, newPolicyId, activeArea), {
                            replace: true,
                        });
                    },
                },
            );
        },
        onSuccess: ({ policyId: newPolicyId }) => {
            toast.success('Policy created');
            setEdits({});
            setFieldErrors({});
            setConfirmOpen(false);
            invalidate();
            navigate(insuranceAreaPath(contractId, newPolicyId, 'agreement'), { replace: true });
        },
        onError: (error: Error) => {
            captureError(error);
            // A FAILED SEQUENCE IS STILL A SEQUENCE THAT WROTE SOMETHING. `createInsurance` rolls
            // back exactly two statements and leaves everything after the junction standing, so the
            // policy, the premium or the loan may all be on the server while this sentence is being
            // read. Reading them back is what lets the retry plan updates and adopts instead of
            // refusing on rows this same page created.
            invalidate();
            toast.error(error.message || 'Failed to create the policy');
            attributeAcrossAreas(error.message);
            setCertificateEnding((ending) => ({ outcome: 'refused', seq: ending.seq + 1 }));
        },
    });

    /**
     * THE EDIT — one bar per area, every write an insert-or-adopt/update against the junction.
     *
     * 54 live junctions carry no loan at all, so the advance area's first save is what CREATES it;
     * the premium area's clear deletes a discount link; the advance area's cleared weekly pair over
     * a saved row deletes it. All three are the writer's, not this page's — what happens here is the
     * choice of which area's plan to run and what to do with the answer.
     */
    const save = useMutation({
        mutationFn: (area: InsuranceArea) => {
            if (!existing) throw new Error('This policy is still loading. Try that again in a moment.');
            const context: InsuranceSaveContext = {
                contractId,
                otherDeposits,
                // AND THE CONTRACT'S HIRE PURCHASE CLASS [D-B57], which is what the writer's own
                // re-validation asks before it lets a financed premium through with no recovery.
                hpClass,
                // THE WRITER'S OWN COPY OF THE FREEZE [F7], not the canvas's. The policy bar stands
                // on a frozen year for the insured value and the certificate; this is what keeps
                // the cover period off the statement it would otherwise be written by.
                frozen,
            };
            if (area === 'policy') {
                // THE CAR IS ASKED FOR HERE AND NOWHERE ELSE ON THIS BAR. The certificate this area
                // files is checked against the contract's plate and chassis, so a policy save needs
                // one; a premium or an advance save opens neither field, and refusing those two on
                // a contract with no vehicle recorded was refusing them over a value they never
                // read. `InsurancePolicySaveContext` is the compiler keeping that straight.
                const subject = requireVehicle();
                return savePolicyArea(
                    supabase,
                    {
                        ...draft,
                        attestation:
                            certificateRef.current?.attestationFor(certificateDates) ?? null,
                    },
                    existing,
                    {
                        ...context,
                        vehicleId: subject.id,
                        vehicle: { plate: subject.plate, vin: subject.vin },
                    },
                );
            }
            if (area === 'premium') return savePremiumArea(supabase, draft, existing, context);
            if (area === 'advance') return saveAdvanceArea(supabase, draft, existing, context);
            throw new Error('Nothing on this area is saved from a bar.');
        },
        onSuccess: (_result, area) => {
            toast.success('Policy updated');
            setFieldErrors({});
            // ONLY THIS AREA'S FIELDS, AND ON A FROZEN YEAR ONLY THE ONES THE WRITE OPENED [F7].
            // The bars are per area, so a premium save must not throw away an advance figure typed
            // beside it and not yet written — and `policyValues` drops the period while frozen, so
            // the narrowed list is what this save actually wrote. It is the list the bar was raised
            // by (`insuranceAreaDirty`) and the list a Discard clears; three readings of one save's
            // field set have to be one reading.
            setEdits((current) => {
                const next = { ...current };
                for (const field of insuranceAreaFields(area, frozen)) delete next[field];
                return next;
            });
            if (area === 'policy') {
                setCertificateEnding((ending) => ({ outcome: 'committed', seq: ending.seq + 1 }));
            }
            invalidate();
        },
        onError: (error: Error, area) => {
            captureError(error);
            // THE ADVANCE AREA IS THREE STATEMENTS AND THEY ARE NOT WRAPPED. A loan that inserted
            // before its instalment was refused is a loan on the server, and a retry planned against
            // the stale read would insert it a second time — `UNIQUE (insurance_contract_id)` turns
            // that into a refusal the admin cannot act on. Read the rows back first.
            invalidate();
            toast.error(error.message || 'Failed to save this policy');
            attributeTo(area, error.message);
            if (area === 'policy') {
                setCertificateEnding((ending) => ({ outcome: 'refused', seq: ending.seq + 1 }));
            }
        },
    });

    const remove = useMutation({
        // The last refusal leaves with the attempt it refused: the dialog stays open on a failure,
        // and a second press must not read as having raised the same sentence again.
        onMutate: () => setDeleteRefusal(null),
        mutationFn: () => {
            if (typeof policyId !== 'number') throw new Error('This policy does not exist yet.');
            return deletePolicy(supabase, policyId);
        },
        onSuccess: () => {
            toast.success('Policy deleted');
            invalidate();
            navigate(`/dashboard/contracts/${contractId}?tab=insurance`);
        },
        onError: (error: Error) => {
            captureError(error);
            // THE CASCADE IS ONE STATEMENT AND IT STILL GETS THE REFETCH. A refusal raised partway
            // through a delete that has already moved rows would otherwise leave every screen on
            // this page describing an estate the server no longer holds.
            invalidate();
            // VERBATIM, AND INSIDE THE DIALOG IT WAS RAISED FROM. The server refuses a policy another
            // contract is also on, which is a fact about the estate no sentence written here could
            // improve — and the confirm stays open so it is read beside the button that raised it.
            setDeleteRefusal(error.message || 'The policy could not be deleted.');
        },
    });

    const certificateSaving =
        create.isPending || (save.isPending && save.variables === 'policy');

    const certificateCommit = useMemo<CertificateCommit>(
        () => ({ saving: certificateSaving, ...certificateEnding }),
        [certificateSaving, certificateEnding],
    );

    const certificate = useCertificateRow({
        dates: certificateDates,
        onDateChange,
        // AND THE ROW IS TOLD WHAT `onDateChange` ALREADY DOES [F7]. That callback refuses on a
        // frozen year, which keeps an unsaveable period out of `edits` — but a refusal the row
        // cannot see is still a row offering `Use the certificate's dates` under a period the
        // Policy canvas is drawing as a read. The writer-side guard stays; this is the door.
        datesLocked: frozen,
        file: form.certificateFile,
        onFileChange,
        attestation: form.attestation,
        onAttestation,
        vehicle: { plate: vehicle.plate, vin: vehicle.vin },
        onFile: filedCertificate,
        policyId: typeof policyId === 'number' ? policyId : null,
        loading: policyLoading,
        refetching: policyRefetching,
        commit: certificateCommit,
        // What the attribution map routed onto the upload slot. The row prints its own stage-time
        // refusal first and this one after it.
        serverError: fieldErrors.policy?.certificateFile ?? null,
    });
    certificateRef.current = certificate;
    /** Lifted out of the model so the Discard below depends on the callback and not on the row. */
    const resetCertificate = certificate.reset;

    /**
     * EVERY REASON THIS DRAFT CANNOT BE WRITTEN, IN READING ORDER, AND THE CERTIFICATE IS LAST.
     *
     * `validateDraft` cannot speak for the document: a draft carrying a `File` says nothing about
     * whether the bytes read. The row holds that answer — either the rejection it is already showing
     * or an offer standing un-ticked — and it is appended rather than merged, because it is about
     * the one thing on the page that is not a figure.
     */
    const certificateRefusal =
        certificate.rejection ?? (certificate.standsUnticked ? CERTIFICATE_UNTICKED : null);
    /**
     * AND THE MISSING VEHICLE IS APPENDED LAST, BECAUSE THE BAR CANNOT LEARN IT ANY OTHER WAY.
     *
     * `requireVehicle` throws it from the create and from the policy area's branch, which is right —
     * neither can be written over a car the contract does not have, whatever the screen believes —
     * but a throw is read after the click, on a toast, and the bar spends the whole time before that
     * offering to create a policy that is impossible. It is a fact about the CONTRACT rather than a
     * field on any canvas, so it stands after the page's own refusals and keeps the throw behind it.
     *
     * AND IT IS FILED UNDER `policy` FOR THE SAME REASON THE THROW MOVED THERE. The premium and the
     * advance are saved without a car, so their bars must not be shut by a sentence about one; an
     * area's bar is disabled by its own area's refusals alone, and the create bar takes the lot.
     */
    const allRefusals = useMemo(
        () => [
            ...refusals.map((refusal) => ({ message: refusal.message, area: refusal.area })),
            ...(certificateRefusal
                ? [{ message: certificateRefusal, area: 'policy' as InsuranceArea }]
                : []),
            ...(vehicle.id === null
                ? [{ message: NO_VEHICLE_REFUSAL, area: 'policy' as InsuranceArea }]
                : []),
        ],
        [refusals, certificateRefusal, vehicle.id],
    );

    /**
     * A ROUTE-LESS ENTRY RESOLVES ITSELF, ONCE — `ContractSetup.tsx:152-160`'s latch.
     *
     * GATED ON `badSubject` FIRST. An address naming something that is not a policy gets the error
     * screen, and a latch that ran anyway would replace it with the create flow and take the bad id
     * out of the bar on the way.
     *
     * AND ON THE MISSING JUNCTION FOR THE SAME REASON ONE STEP LOWER: a policy this contract is not
     * on shows its own screen, and appending a canvas name to that address would claim there is a
     * canvas there to land on.
     */
    const landed = useRef(false);
    useEffect(() => {
        if (routeArea) return;
        if (badSubject) return;
        if (!stateReady) return;
        if (junctionMissing) return;
        if (landed.current) return;
        landed.current = true;
        navigate(insuranceAreaPath(contractId, policyId, landingArea), { replace: true });
    }, [
        routeArea,
        badSubject,
        stateReady,
        junctionMissing,
        landingArea,
        contractId,
        policyId,
        navigate,
    ]);

    /**
     * A DIFFERENT POLICY IS A DIFFERENT SUBJECT — except for the one hop that is not.
     *
     * `new` → `<id>` at `onIdentity` is the SAME year of cover halfway through being written: the
     * premium, the loan and the weekly recovery are still in `edits` and still to be sent, and a
     * reset there would blank the canvas under a sequence that is still running.
     */
    const subjectRef = useRef<number | 'new'>(policyId);
    useEffect(() => {
        if (subjectRef.current === policyId) return;
        const wasTheCreateHop = subjectRef.current === 'new' && policyId === createdId.current;
        subjectRef.current = policyId;
        if (wasTheCreateHop) return;
        setEdits({});
        setFieldErrors({});
        setActiveRowId(null);
        /*
         * AND THE POLICY THIS PAGE CREATED IS FORGOTTEN WITH THE DRAFT THAT MADE IT.
         *
         * `createdId` is what closes the create door (`mode === 'create' && createdId === null`) and
         * what tells the hop from a genuine change of subject. Left standing, a walk back to
         * `/insurance/new` from the policy just created would land on the create flow with the door
         * already shut: the bar would offer "Create policy" and the click would raise "This policy
         * has already been created." about a policy that is no longer the subject.
         */
        createdId.current = null;
    }, [policyId]);

    /**
     * A YEAR THAT FREEZES UNDER A TYPED FIGURE DROPS THE FIGURE [F7].
     *
     * `frozen` flips on the FIRST signature, which lands on the server and arrives here on a refetch
     * — so it can turn true while these boxes hold values nobody has saved. From that render both
     * canvases draw those values as READS off `form`, and `form` is the saved values with `edits`
     * laid over them: the canvas would print an unsent draft as the fact the instrument was signed
     * on, and the rail would carry a draft mark for a change no save can ever make.
     *
     * BOTH AREAS ARE COVERED BECAUSE BOTH TURN TO READS, and the Advance canvas is the worse of the
     * two: the Policy canvas at least keeps its bar, so a stranded insured value can still be saved
     * or discarded, but `areaCommit` returns null for the whole advance area once frozen — a
     * premium or a weekly figure left in `edits` there would sit on screen as the signed fact with
     * no bar over it and no Discard to take it back out. Hence `FROZEN_DROPPED_FIELDS`: the period
     * for the one, every field for the other.
     *
     * DROPPED HERE RATHER THAN READ AROUND, because every reader would otherwise need the same
     * exception — the canvases, the rail's draft marks and the bars each ask `form` and each would
     * have to remember. `onDateChange` already refuses once frozen and the advance's fields stop
     * being fields, so nothing can put any of them back; this is the one window those guards cannot
     * close, the edits that were already there when they shut.
     */
    useEffect(() => {
        if (!frozen) return;
        setEdits((current) => {
            if (!FROZEN_DROPPED_FIELDS.some((field) => field in current)) return current;
            const next = { ...current };
            for (const field of FROZEN_DROPPED_FIELDS) delete next[field];
            return next;
        });
    }, [frozen]);

    const areaIndex = INSURANCE_AREAS.findIndex((candidate) => candidate.key === activeArea);
    const area = INSURANCE_AREAS[areaIndex >= 0 ? areaIndex : 0];
    const previousArea = areaIndex > 0 ? INSURANCE_AREAS[areaIndex - 1] : null;
    const nextArea =
        areaIndex >= 0 && areaIndex < INSURANCE_AREAS.length - 1
            ? INSURANCE_AREAS[areaIndex + 1]
            : null;

    const selectRow = useCallback(
        (row: InsuranceRow) => {
            setActiveRowId(row.id);
            setSheetOpen(false);
            if (row.jump.area !== activeArea) goToArea(row.jump.area);
            focusJumpTarget(rootRef.current, row.jump.block, row.jump.focus);
        },
        [activeArea, goToArea, rootRef],
    );

    useEffect(() => {
        if (!activeRowId) return;
        const selected = state.rows.find((candidate) => candidate.id === activeRowId);
        if (selected && selected.area !== activeArea) setActiveRowId(null);
    }, [activeArea, activeRowId, state.rows]);

    /**
     * WHERE A PRE-FLIGHT OR A LOCKED SLOT SENDS THE ADMIN, AND IT IS TOTAL OVER THE VOCABULARY.
     *
     * THE FIELD RIDES ALONG BECAUSE THE STEP IS NOT ENOUGH HERE. `MERGE_FIELD_STEPS` routes the
     * whole `insurance.` and `insurance_loan.` namespaces to the single step `insurance`, and this
     * dossier is three canvases; `resolveInsuranceDeepLink` splits that step by field and falls
     * through to the contract's own setup route for everything else — always a real path.
     */
    const onDeepLink = useCallback(
        (value: string, field?: string) => {
            const target = resolveInsuranceDeepLink(contractId, value, field);
            if (target.kind === 'insurance') {
                goToArea(target.area);
                return;
            }
            navigate(target.path);
        },
        [contractId, goToArea, navigate],
    );

    const onGoToSetupArea = useCallback(
        (setupArea: SetupArea) => navigate(areaPath(contractId, setupArea)),
        [contractId, navigate],
    );

    const onGoToPolicy = useCallback(() => goToArea('policy'), [goToArea]);

    /** The confirm was dismissed: the sentence it was raised in goes with it. */
    const onDeleteDialogClose = useCallback(() => setDeleteRefusal(null), []);

    const discardArea = useCallback(
        (target: InsuranceArea) => {
            setEdits((current) => {
                const next = { ...current };
                // NARROWED BY THE FREEZE, as the bar above it is [F7]: on a frozen year the period
                // is not this area's to clear, because it is not this area's to write.
                for (const field of insuranceAreaFields(target, frozen)) delete next[field];
                return next;
            });
            // ONLY THIS AREA'S REFUSALS, for the reason its edits are only this area's: the bars are
            // per area, and a Discard on the premium canvas that wiped the whole map would take the
            // sentence off an advance figure it never touched — leaving a refused field looking
            // accepted until the next save raises it again.
            setFieldErrors((current) => {
                if (current[target] === undefined) return current;
                const next = { ...current };
                delete next[target];
                return next;
            });
            // THE CERTIFICATE ROW CLEARS ITSELF, AND ONLY FROM THE AREA THAT OWNS IT: the typed
            // plate, the attestation, a read in flight, the on-file handle and the prefill that
            // names it. A Discard on the premium canvas has nothing to do with the document, and
            // retiring its read from there would spend a signed URL to put the same sentence back.
            if (target === 'policy') resetCertificate();
        },
        [resetCertificate, frozen],
    );

    /**
     * What the create bar says while the writer would refuse.
     *
     * IT NAMES ONE AND COUNTS THE REST, AND IT NAMES WHERE THAT ONE LIVES: the refusal list runs in
     * reading order across three canvases, and an admin told only "something is missing" on the
     * Advance canvas would go looking for it there.
     */
    const createHint = (() => {
        if (allRefusals.length === 0) {
            return 'Writes the policy, the premium and the advance together.';
        }
        const [first] = allRefusals;
        const where = INSURANCE_AREA_LABELS[first.area];
        const rest = allRefusals.length - 1;
        return rest > 0
            ? `${first.message} (${where}) · ${rest} more to fill in`
            : `${first.message} (${where})`;
    })();

    const areaCommit = (target: InsuranceArea) => {
        // NOTHING IS TYPED ON THE AGREEMENT CANVAS. Its work is signing, and the agreement area's
        // field list is empty — a bar there would have nothing to commit and nothing to discard.
        if (target === 'agreement') return null;

        // NO BAR WHILE THE BOARD IS REFUSED, AND ONLY ON THE ADVANCE. That canvas's arithmetic is
        // composed from the contract's OTHER standing advances, which are read off the board's
        // subjects: after a refused read that list is empty rather than absent, so the forward
        // weekly total would be understated on the one canvas that states it. The policy and
        // premium canvases write columns the board has nothing to say about.
        if (agreements.failed && target === 'advance') return null;

        if (mode === 'create') {
            // ONE CREATE BAR, AND IT LIVES ON THE ADVANCE CANVAS — the last of the three that states
            // figures, so the commit stands under the last thing typed rather than halfway up.
            if (target !== 'advance') return null;
            return (
                <CommitBar
                    saving={create.isPending}
                    saveLabel="Create policy"
                    hint={createHint}
                    disabled={allRefusals.length > 0}
                    onSave={() => setConfirmOpen(true)}
                />
            );
        }

        // THE SEQUENCE HAS HOPPED AND IS STILL RUNNING. Between `onIdentity` and the last statement
        // this page is an edit screen over a policy whose premium and loan are in flight — a bar
        // offering to save the same figures would race the writer with the writer's own draft.
        if (create.isPending) return null;

        // AND NO BAR UNTIL EVERY READ THIS POLICY IS JUDGED AGAINST HAS ANSWERED. `existing` is the
        // ids an edit is written against and `frozen` comes off the board's slot; while either is
        // still in flight both read as "nothing there", so a frozen year would offer a save because
        // the freeze had not arrived.
        if (!stateReady) return null;
        if (junctionMissing) return null;

        // FROZEN IS THE ADVANCE CANVAS ALONE [F7]. Both instruments print the money grid, so a
        // signed one cannot have it moved underneath it; neither prints the insured value or the
        // certificate, so the policy canvas keeps its bar for exactly those two — and its period
        // fields are already reads.
        if (frozen && target === 'advance') return null;

        // THE POLICY BAR ON A FROZEN YEAR ASKS THE NARROWED LIST [F7]. `policyValues` writes the
        // insured value and the certificate there and drops the period, so a bar raised by a date
        // would offer to save a change the writer throws away.
        if (!insuranceAreaDirty(target, saved, edits, frozen)) return null;

        /*
         * AN EDIT BAR ANSWERS FOR ITS OWN AREA AND FOR NOTHING ELSE, which is the writer's own rule
         * read back onto the control. `savePolicyArea` and its two siblings filter the refusals down
         * to their area before they write, so a bar disabled by another canvas's refusal is refusing
         * a save the writer would have taken. And the refusals are not hypothetical: 50 live
         * policies carry no insured value, every one of them on a loan-less junction, so a page that
         * disabled all four bars over one area's sentence would be unusable on most of the estate.
         * The create bar keeps the whole list, because a create writes all three areas at once.
         */
        const areaRefusals = allRefusals.filter((refusal) => refusal.area === target);
        const areaRefusal = areaRefusals[0] ?? allRefusals[0] ?? null;
        return (
            <CommitBar
                saving={save.isPending && save.variables === target}
                hint={
                    areaRefusal
                        ? `${areaRefusal.message} (${INSURANCE_AREA_LABELS[areaRefusal.area]})`
                        : 'Unsaved changes on this block.'
                }
                disabled={areaRefusals.length > 0}
                onSave={() => save.mutate(target)}
                onDiscard={() => discardArea(target)}
            />
        );
    };

    const areaContent = () => {
        /*
         * THE SPINNER IS ABOUT THE READS, NOT ABOUT THE URL. Every other way in carries an area
         * already — a bookmark, browser Back, the Insurance tab's own CTA, the landing latch's
         * target — and until the policy, the chain count, the contract and the board have answered,
         * the canvases would state a year that is not this one: the instrument is decided off the
         * draft, and an empty draft is an ADVANCE (`instrumentFor` answers that for a premium of
         * none) over a year that may well have been settled in full and papered as a Record.
         */
        if (!stateReady) {
            return (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            );
        }

        switch (area.key) {
            case 'policy':
                return (
                    <PolicyArea
                        mode={mode}
                        frozen={frozen}
                        instrument={instrument}
                        form={form}
                        onChange={onChange}
                        certificate={certificate}
                        errors={fieldErrors.policy}
                        chainLength={snapshot.chainLength}
                        onDelete={isNew ? undefined : () => remove.mutate()}
                        deleting={remove.isPending}
                        deleteRefusal={deleteRefusal}
                        onDeleteDialogClose={onDeleteDialogClose}
                        commit={areaCommit('policy')}
                    />
                );
            case 'premium':
                return (
                    <PremiumArea
                        mode={mode}
                        form={form}
                        onChange={onChange}
                        errors={fieldErrors.premium}
                        commit={areaCommit('premium')}
                    />
                );
            case 'advance':
                return (
                    <AdvanceArea
                        mode={mode}
                        frozen={frozen}
                        instrument={instrument}
                        form={form}
                        onChange={onChange}
                        otherDeposits={otherDeposits}
                        instalmentSaved={instalmentSaved}
                        snapshot={snapshot}
                        forward={forward}
                        errors={fieldErrors.advance}
                        commit={areaCommit('advance')}
                    />
                );
            case 'agreement':
                return (
                    <InsuranceAgreementArea
                        policyId={policyId}
                        agreements={agreements}
                        slots={insuranceSlotsForLoan(agreements.slots, existing?.loanId)}
                        hasLoan={existing?.loanId !== null && existing?.loanId !== undefined}
                        instrument={instrument}
                        driver={snapshot.driver}
                        vehicle={snapshot.vehicle}
                        period={policyPeriodText(form.startsAt, form.expiresAt)}
                        hpReference={snapshot.hpReference}
                        hpDriverSigned={snapshot.hpDriverSigned}
                        onDeepLink={onDeepLink}
                        onGoToSetupArea={onGoToSetupArea}
                        onGoToPolicy={onGoToPolicy}
                    />
                );
            default:
                return null;
        }
    };

    const footerNode = <div className="sd-gate-note">{footerNote(state.progress)}</div>;

    const backToInsurance = (
        <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => navigate(`/dashboard/contracts/${contractId}?tab=insurance`)}
        >
            Back to Insurance
        </Button>
    );

    if (badSubject) {
        return (
            <div className="space-y-4 px-4 pt-6 sm:px-6 lg:px-8">
                <p className="text-lg">That is not a policy.</p>
                <p className="text-sm text-muted-foreground">
                    The address names <code>{policyIdParam}</code>, and a policy is identified by a
                    number (or by the word <code>new</code>).
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard/contracts/${contractId}?tab=insurance`)}
                >
                    Back to Insurance
                </Button>
            </div>
        );
    }

    /*
     * A REAL POLICY ID WHOSE JUNCTION READ ANSWERED NULL — cover this contract is not on.
     *
     * IT IS A PAGE STATE AND NOT AN ERROR: a hand-edited address, a stale bookmark or a policy
     * deleted from another tab all land here, and none of them is a failure of anything. No canvas
     * is mounted, because every one of them would be describing another contract's year: the
     * agreement board would draw this contract's own slots beside a policy it has never heard of.
     */
    if (junctionMissing) {
        return (
            <div className="space-y-4 px-4 pt-6 sm:px-6 lg:px-8">
                <p className="text-lg">This contract is not on that policy.</p>
                <p className="text-sm text-muted-foreground">
                    Policy #{policyIdParam} exists or it does not, but this contract has no year of
                    cover on it — so there is nothing here to edit.
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard/contracts/${contractId}?tab=insurance`)}
                >
                    Back to Insurance
                </Button>
            </div>
        );
    }

    return (
        <AgreementSealingProvider value={sealing}>
            <div className="setup-dossier sd-no-ledger -m-4 lg:-m-6" ref={rootRef}>
                <Manifest
                    state={state}
                    activeArea={activeArea}
                    activeRowId={activeRowId}
                    onSelectRow={selectRow}
                    footer={footerNode}
                    head={
                        <header className="sd-hdr">
                            <div className="sd-hdr-top">
                                <h1 className="min-w-0 text-4xl font-semibold tracking-tight">
                                    {insuranceHeading(policyId)}
                                </h1>
                                {/* Below 1024px the journey is a sheet, and this is the way into it. */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-auto h-8 flex-none lg:hidden"
                                    aria-expanded={sheetOpen}
                                    onClick={() => setSheetOpen(true)}
                                >
                                    {journeyLabel(state.progress)}
                                </Button>
                            </div>

                            <NarratorLine
                                state={state}
                                onJump={selectRow}
                                /* NO ACTIVATION AND NO RECEIPT ON THIS RAIL. `computeInsuranceState`
                                   emits neither intent — a policy has no ceremony to open and no
                                   receipt to re-open — so these two can never be reached, and they
                                   are wired rather than stubbed silently so that an action added to
                                   the insurance narrator without a handler fails loudly here. */
                                onActivate={() => undefined}
                                onOpenReceipt={() => undefined}
                            />

                            {/* THE WAY BACK IS A LINK IN THE HEAD, and it lands on the tab the
                                policy was opened from — which is why that tab is in the URL. */}
                            <div className="sd-hdr-actions">{backToInsurance}</div>
                        </header>
                    }
                />

                <main className="sd-canvas-wrap">
                    <div className="sd-canvas">
                        <h2 className="sd-area-h">{area.label}</h2>
                        <p className="sd-area-q">{area.question}</p>

                        {areaContent()}

                        <nav className="sd-area-foot" aria-label="Areas">
                            {previousArea ? (
                                <button
                                    type="button"
                                    className="sd-af-btn"
                                    onClick={() => goToArea(previousArea.key)}
                                >
                                    <span>Previous</span>
                                    {previousArea.label}
                                </button>
                            ) : (
                                <span />
                            )}
                            {nextArea && (
                                <button
                                    type="button"
                                    className="sd-af-btn sd-af-btn-right"
                                    onClick={() => goToArea(nextArea.key)}
                                >
                                    <span>Next</span>
                                    {nextArea.label}
                                </button>
                            )}
                        </nav>
                    </div>
                </main>

                {/* Below 1024px the manifest is this sheet, behind the persistent Journey button. */}
                <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
                    <DrawerContent className="sd-tokens h-[78vh] max-h-[78vh]">
                        <DrawerTitle className="sr-only">Journey manifest</DrawerTitle>
                        <Manifest
                            state={state}
                            activeArea={activeArea}
                            activeRowId={activeRowId}
                            onSelectRow={selectRow}
                            footer={footerNode}
                            variant="sheet"
                        />
                    </DrawerContent>
                </Drawer>

                {/* MOUNTED BY THE PAGE, NOT BY THE BAR THAT OPENS IT. It is a Radix `AlertDialog`
                    portalled to `document.body`, and the bar lives on a canvas that unmounts on
                    every area switch — a dialog mounted from a control that can go away is a dialog
                    that closes itself mid-confirmation. */}
                <CreateInsuranceConfirm
                    open={confirmOpen}
                    onOpenChange={setConfirmOpen}
                    draft={draft}
                    snapshot={snapshot}
                    depositTotal={draftDepositTotal(form, otherDeposits)}
                    pending={create.isPending}
                    blocked={allRefusals.length > 0}
                    onConfirm={() => create.mutate()}
                />
            </div>
        </AgreementSealingProvider>
    );
}

/**
 * The manifest footer's ONE LINE, and it is a state and never a control.
 *
 * `.sd-manifest-foot` is `display: none` below 1024px, so anything that WRITES cannot live here:
 * the create button would simply not exist on a phone. What belongs is the same count the journey
 * trigger carries, said in full where there is room for it.
 */
function footerNote(progress: SetupProgress): string {
    if (progress.outstanding === 0) return 'Nothing outstanding on this policy.';
    const parts = [`${progress.outstanding} open`];
    if (progress.needYou > 0) parts.push(`${progress.needYou} need you`);
    if (progress.waitingOnOthers > 0) parts.push(`${progress.waitingOnOthers} waiting on others`);
    return parts.join(' · ');
}
